import React, { useState } from 'react';
import { Container, Button, Form, Nav, Card, Row, Col } from 'react-bootstrap';
import { Select } from 'antd';
import { FaRegClock, FaTruck, FaCheckCircle, FaTag, FaPlus, FaBoxOpen } from 'react-icons/fa';
import PageTitle from '@/common/PageTitle/PageTitle';
import FormItem from '@/common/FormItem/FormItem';
import CreateLabelModal from '@/components/CreateLabelModal';
import AddShipmentModal, { NewOrderData } from '@/components/AddShipmentModal';
import OrderProductsModal from '@/components/OrderProductsModal';
import { UPSShipmentResponse } from '@/services/upsService';
import { initialOrders, Order, ShippingStatus } from '@/data/orders';
import styles from '@/styles/inventory-shipping.module.scss';
import upsLogo from '@/assets/images/upslogo.png';

const statusStyles: Record<ShippingStatus, {
  color: string;
  background: string;
  icon: any;
  label: string;
}> = {
  Pending: {
    color: '#e21e26',
    background: 'rgba(226,30,38,0.12)',
    icon: FaRegClock({ className: styles.statusIcon, style: { color: '#e21e26', fontSize: '12px' } }),
    label: 'Pending',
  },
  'Label Created': {
    color: '#9333ea',
    background: 'rgba(147,51,234,0.12)',
    icon: FaCheckCircle({ className: styles.statusIcon, style: { color: '#9333ea', fontSize: '12px' } }),
    label: 'Label Created',
  },
  Shipped: {
    color: '#2d72d9',
    background: 'rgba(45,114,217,0.12)',
    icon: FaTruck({ className: styles.statusIcon, style: { color: '#2d72d9', fontSize: '12px' } }),
    label: 'Shipped',
  },
  Delivered: {
    color: '#1db954',
    background: 'rgba(29,185,84,0.12)',
    icon: FaCheckCircle({ className: styles.statusIcon, style: { color: '#1db954', fontSize: '12px' } }),
    label: 'Delivered',
  },
  Cancelled: {
    color: '#6b7280',
    background: 'rgba(107,114,128,0.12)',
    icon: FaRegClock({ className: styles.statusIcon, style: { color: '#6b7280', fontSize: '12px' } }),
    label: 'Cancelled',
  },
  'Action Required': {
    color: '#f59e0b',
    background: 'rgba(245,158,11,0.12)',
    icon: FaRegClock({ className: styles.statusIcon, style: { color: '#f59e0b', fontSize: '12px' } }),
    label: 'Action Req',
  },
  'Awaiting Pickup': {
    color: '#06b6d4',
    background: 'rgba(6,182,212,0.12)',
    icon: FaRegClock({ className: styles.statusIcon, style: { color: '#06b6d4', fontSize: '12px' } }),
    label: 'Awaiting Pickup',
  },
};

const tabs = [
  'Incoming Orders',
  'Created Labels',
  'Shipped',
  'Cancelled',
  'Action Required',
  'Awaiting Pickup',
];

const ShippingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('Incoming Orders');
  const [showCreateLabelModal, setShowCreateLabelModal] = useState(false);
  const [showAddShipmentModal, setShowAddShipmentModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orders, setOrders] = useState<Order[]>(initialOrders);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [dateFilter, setDateFilter] = useState<string | undefined>(undefined);
  
  // Map tab names to order statuses and apply search filter
  const getFilteredOrders = () => {
    let filtered: Order[] = [];
    
    // First filter by tab/status
    switch (activeTab) {
      case 'Incoming Orders':
        filtered = orders.filter(order => order.status === 'Pending');
        break;
      case 'Created Labels':
        filtered = orders.filter(order => order.status === 'Label Created');
        break;
      case 'Shipped':
        filtered = orders.filter(order => order.status === 'Shipped' || order.status === 'Delivered');
        break;
      case 'Cancelled':
        filtered = orders.filter(order => order.status === 'Cancelled');
        break;
      case 'Action Required':
        filtered = orders.filter(order => order.status === 'Action Required');
        break;
      case 'Awaiting Pickup':
        filtered = orders.filter(order => order.status === 'Awaiting Pickup');
        break;
      default:
        filtered = orders;
    }
    
    // Then apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(order => 
        order.name.toLowerCase().includes(searchLower) ||
        order.orderNumber.toLowerCase().includes(searchLower) ||
        order.tracking.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply date range filter
    if (dateFilter) {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case '7days':
          filterDate.setDate(now.getDate() - 7);
          break;
        case '30days':
          filterDate.setDate(now.getDate() - 30);
          break;
        case '90days':
          filterDate.setDate(now.getDate() - 90);
          break;
        case 'year':
          filterDate.setFullYear(now.getFullYear(), 0, 1); // Jan 1st of current year
          break;
        default:
          break;
      }
      
      if (dateFilter) {
        filtered = filtered.filter(order => {
          // Parse date in DD/MM/YYYY format
          const [day, month, year] = order.orderDate.split('/').map(Number);
          const orderDate = new Date(year, month - 1, day);
          return orderDate >= filterDate;
        });
      }
    }
    
    return filtered;
  };
  
  const filteredOrders = getFilteredOrders();
  
  const handleCreateLabel = (order: Order) => {
    setSelectedOrder(order);
    setShowCreateLabelModal(true);
  };

  const handleViewProducts = (order: Order) => {
    setSelectedOrder(order);
    setShowProductsModal(true);
  };
  
  const handleLabelSuccess = (response: UPSShipmentResponse) => {
    console.log('Label created successfully!', response);
    
    // Download the label image as a file
    if (response.graphicImage) {
      const linkSource = `data:image/${response.labelImageFormat.toLowerCase()};base64,${response.graphicImage}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = `shipping-label-${response.trackingNumber}.${response.labelImageFormat.toLowerCase()}`;
      downloadLink.click();
    }
    
    // Update the order in local state
    if (selectedOrder) {
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.orderNumber === selectedOrder.orderNumber
            ? {
                ...order,
                status: 'Label Created' as ShippingStatus,
                tracking: response.trackingNumber,
              }
            : order
        )
      );
      
      // Switch to "Created Labels" tab to show the updated order
      setActiveTab('Created Labels');
    }
    
    alert(`Label created successfully!\n\nTracking Number: ${response.trackingNumber}\nTotal Cost: ${response.totalCharges.currencyCode} ${response.totalCharges.monetaryValue}\n\nThe label image has been downloaded.\n\nThe order has been moved to "Created Labels" tab.`);
    
    // TODO: When using real API, update order in backend
    // await fetch(`${process.env.REACT_APP_BASE_API_URL}/v1/orders/${selectedOrder?.orderNumber}/tracking`, {
    //   method: 'PATCH',
    //   headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     trackingNumber: response.trackingNumber,
    //     labelImage: response.graphicImage,
    //     shippingCost: response.totalCharges.monetaryValue,
    //     carrier: 'UPS'
    //   })
    // });
  };
  
  const handleAddShipment = (newOrderData: NewOrderData) => {
    // Generate a unique order number if not provided
    const orderNumber = newOrderData.orderNumber || `#ABCD-${Date.now().toString().slice(-7)}`;
    
    // Calculate total amount
    const totalAmount = newOrderData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // Create new order object
    const newOrder: Order = {
      key: orders.length + 1,
      processing: true,
      name: newOrderData.name,
      status: 'Pending',
      orderDate: newOrderData.orderDate,
      orderNumber: orderNumber,
      tracking: '',
      shippingMethod: 'UPS',
      shippingLogo: upsLogo,
      country: '🇺🇸', // Default to US flag, could map from country code
      items: newOrderData.items,
      totalAmount: totalAmount,
    };
    
    // Add to orders list
    setOrders(prevOrders => [newOrder, ...prevOrders]);
    
    // Switch to Incoming Orders tab to show the new order
    setActiveTab('Incoming Orders');
    
    const itemsSummary = newOrderData.items.map(item => `${item.quantity}x ${item.productName}`).join(', ');
    alert(`Shipment created successfully!\n\nOrder Number: ${orderNumber}\nRecipient: ${newOrderData.name}\nProducts: ${itemsSummary}\nTotal: $${totalAmount.toFixed(2)}\n\nThe order is now ready for label creation.`);
    
    // TODO: When using real API, create order in backend
    // await fetch(`${process.env.REACT_APP_BASE_API_URL}/v1/orders`, {
    //   method: 'POST',
    //   headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    //   body: JSON.stringify(newOrderData)
    // });
  };
  
  return (
    <Container fluid>
      <Card className={styles.card}>
        <Card.Body>
          <Card.Title className={styles.cardTitle}>
            <PageTitle title="Shipping Items" />
          </Card.Title>
      
      <Nav variant="tabs" className={styles.navTabs}>
        {tabs.map(tab => (
          <Nav.Item key={tab}>
            <Nav.Link
              active={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className={`${styles.navLink} ${activeTab === tab ? styles.navLinkActive : ''}`}
            >
              {tab}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      
      {/* Filters row - matching Albums page layout */}
      <Row className="mb-2" style={{ marginTop: '24px' }}>
        <Col>
          <Row>
            <Col md={4}>
              <FormItem
                label="Search Orders"
                className="customSelectWrapper"
                layout="vertical"
              >
                <Select
                  showSearch
                  allowClear
                  className="customSelect"
                  placeholder="Search by name, order number, tracking..."
                  value={searchText}
                  onChange={(value) => setSearchText(value)}
                  onSearch={(value) => {
                    // Allow real-time search as user types
                    if (!value) {
                      setSearchText(undefined);
                    }
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={[
                    ...orders.map(order => ({
                      label: order.name,
                      value: order.name,
                    })).filter((option, index, self) => 
                      // Remove duplicates
                      index === self.findIndex((t) => t.value === option.value)
                    ),
                  ]}
                />
              </FormItem>
            </Col>
            <Col md={4}>
              <FormItem
                label="Date Range"
                className="customSelectWrapper"
                layout="vertical"
              >
                <Select
                  allowClear
                  className="customSelect"
                  placeholder="Select date range"
                  value={dateFilter}
                  onChange={(value) => setDateFilter(value)}
                  options={[
                    { label: 'Last 7 days', value: '7days' },
                    { label: 'Last 30 days', value: '30days' },
                    { label: 'Last 90 days', value: '90days' },
                    { label: 'This year', value: 'year' },
                  ]}
                />
              </FormItem>
            </Col>
            <Col md={4} className="d-flex align-items-center mt-2 mt-md-0">
              <Button
                className="w-100"
                onClick={() => setShowAddShipmentModal(true)}
                style={{ 
                  background: 'white', 
                  color: '#e21e26', 
                  border: '1px solid #e21e26', 
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  justifyContent: 'center',
                  height: '50px',
                  marginTop: '8px'
                }}
              >
                {React.createElement(FaPlus as any, { style: { fontSize: 16 } })}
                Add New Shipment
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.tableHeaderRow}>
              <th className={styles.checkboxCell}></th>
              <th className={styles.tableHeaderCell}>Status</th>
              <th className={styles.tableHeaderCell}>Name</th>
              <th className={styles.tableHeaderCell}>Ship Status</th>
              <th className={styles.tableHeaderCell}>Order Date</th>
              <th className={styles.tableHeaderCell}>Order Number</th>
              <th className={styles.tableHeaderCell}>Tracking Number</th>
              <th className={styles.tableHeaderCell}>Products</th>
              <th className={styles.tableHeaderCell}>Shipping</th>
              <th className={styles.tableHeaderCell}>Country</th>
              <th className={styles.tableHeaderCellCenter}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order, idx) => (
                <tr key={order.key} className={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
                  <td className={styles.checkboxCell}>
                    <input type="checkbox" className={styles.checkbox} />
                  </td>
                  <td className={styles.tableCell}>Ready to process</td>
                  <td className={styles.tableCell}>{order.name}</td>
                  <td className={styles.tableCell}>
                    <span className={`${styles.statusBadge} ${
                      order.status === 'Pending' ? styles.statusPending :
                      order.status === 'Shipped' ? styles.statusShipped :
                      order.status === 'Delivered' ? styles.statusDelivered :
                      order.status === 'Cancelled' ? styles.statusCancelled :
                      order.status === 'Label Created' ? styles.statusLabelCreated :
                      order.status === 'Action Required' ? styles.statusActionRequired :
                      styles.statusAwaitingPickup
                    }`}>
                      {statusStyles[order.status].icon}
                      {statusStyles[order.status].label}
                    </span>
                  </td>
                  <td className={styles.tableCell}>{order.orderDate}</td>
                  <td className={styles.tableCell}>{order.orderNumber}</td>
                  <td className={styles.tableCell}>
                    {order.tracking === '' ? <span className={styles.notShipped}>Not shipped</span> : order.tracking}
                  </td>
                  <td 
                    className={styles.productsCell}
                    onClick={() => handleViewProducts(order)}
                    title="Click to view products"
                  >
                    <div className={styles.productsInfo}>
                      {React.createElement(FaBoxOpen as any, { className: styles.productsIcon })}
                      <span>{order.items.length} {order.items.length === 1 ? 'item' : 'items'}</span>
                    </div>
                  </td>
                  <td className={styles.tableCell}>
                    <div className={styles.shippingMethod}>
                      <img src={order.shippingLogo} alt={order.shippingMethod} className={styles.shippingLogo} />
                      {order.shippingMethod}
                    </div>
                  </td>
                  <td className={styles.country}>{order.country}</td>
                  <td className={styles.tableCellCenter}>
                    {order.status === 'Pending' ? (
                      <Button
                        className={styles.createLabelBtn}
                        onClick={() => handleCreateLabel(order)}
                        title="Create Shipping Label"
                      >
                        {React.createElement(FaTag as any)} Label
                      </Button>
                    ) : (
                      React.createElement(FaCheckCircle as any, { className: styles.actionIcon })
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={11} className={styles.noResults}>
                  No orders found for this status
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className={styles.paginationContainer}>
          <span className={styles.paginationInfo}>Showing 1 - {filteredOrders.length} of {filteredOrders.length}</span>
          <div className={styles.paginationButtons}>
            <Button className={styles.paginationButton}>1</Button>
            <Button className={styles.paginationButton}>2</Button>
            <Button className={styles.paginationButton}>3</Button>
            <Button className={styles.paginationButton}>{'>'}</Button>
          </div>
        </div>
      </div>
        </Card.Body>
      </Card>
      
      {/* Create Label Modal */}
      <CreateLabelModal
        show={showCreateLabelModal}
        onHide={() => setShowCreateLabelModal(false)}
        onSuccess={handleLabelSuccess}
        orderData={selectedOrder ? {
          orderNumber: selectedOrder.orderNumber,
          name: selectedOrder.name,
          orderDate: selectedOrder.orderDate,
        } : undefined}
      />
      
      {/* Add Shipment Modal */}
      <AddShipmentModal
        show={showAddShipmentModal}
        onHide={() => setShowAddShipmentModal(false)}
        onSuccess={handleAddShipment}
      />
      
      {/* Order Products Modal */}
      <OrderProductsModal
        show={showProductsModal}
        onHide={() => setShowProductsModal(false)}
        orderNumber={selectedOrder?.orderNumber || ''}
        items={selectedOrder?.items || []}
      />
    </Container>
  );
};

export default ShippingPage; 